<template>
  <div class="team-management">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">
              <i class="fas fa-users mr-2"></i>
              Team Management
            </h4>
          </div>
          <div class="card-body">
            <div v-if="$apollo.loading" class="text-center">
              <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
              </div>
            </div>

            <div v-else>
              <!-- Teams Overview -->
              <div class="row mb-4">
                <div class="col-12">
                  <h5>Teams Overview</h5>
                  <div v-if="teams && teams.length > 0" class="table-responsive">
                    <table class="table table-striped">
                      <thead>
                        <tr>
                          <th>Team Name</th>
                          <th class="d-none d-sm-table-cell">Key</th>
                          <th class="d-none d-md-table-cell">Company</th>
                          <th>Members</th>
                          <th>Devices</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="team in teams" :key="team.id">
                          <td><strong>{{ team.name }}</strong></td>
                          <td class="d-none d-sm-table-cell"><code>{{ team.key }}</code></td>
                          <td class="d-none d-md-table-cell">{{ team.company ? team.company.name : 'N/A' }}</td>
                          <td>
                            <b-badge variant="info">
                              {{ getTeamMemberCount(team.id) }}
                            </b-badge>
                          </td>
                          <td>
                            <b-badge variant="success">
                              {{ getTeamDeviceCount(team.id) }}
                            </b-badge>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else class="text-center text-muted">
                    <p>No teams found.</p>
                  </div>
                </div>
              </div>

              <!-- Team Members -->
              <div class="row">
                <div class="col-12">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h5 class="mb-0">Team Members</h5>
                    <b-button variant="success" size="sm" @click="openAddMemberModal">
                      <i class="fas fa-user-plus mr-1"></i> Add Member
                    </b-button>
                  </div>
                  <div v-if="teamMembers && teamMembers.length > 0" class="table-responsive">
                    <table class="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th class="d-none d-sm-table-cell">Email</th>
                          <th>Team</th>
                          <th>Role</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="member in teamMembers" :key="member.id">
                          <td><strong>{{ member.user.firstName }} {{ member.user.lastName }}</strong></td>
                          <td class="d-none d-sm-table-cell">{{ member.user.email }}</td>
                          <td>
                            <b-badge variant="primary">{{ member.team.name }}</b-badge>
                          </td>
                          <td>
                            <b-badge :variant="getRoleVariant(member.role)">{{ member.role }}</b-badge>
                          </td>
                          <td class="text-right">
                            <b-button size="sm" variant="outline-danger" @click="confirmRemoveMember(member)" title="Remove">
                              <i class="fas fa-user-minus"></i>
                            </b-button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else class="text-center text-muted">
                    <p>No team members found.</p>
                  </div>
                </div>
              </div>

              <!-- Team Devices -->
              <div class="row mt-4">
                <div class="col-12">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h5 class="mb-0">Team Device Assignments</h5>
                    <b-button variant="success" size="sm" @click="openAddDeviceModal">
                      <i class="fas fa-plus mr-1"></i> Add Device
                    </b-button>
                  </div>
                  <div v-if="teamDevices && teamDevices.length > 0" class="table-responsive">
                    <table class="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>Device</th>
                          <th class="d-none d-sm-table-cell">Serial</th>
                          <th class="d-none d-md-table-cell">Type</th>
                          <th>Team</th>
                          <th>Permission</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="teamDevice in teamDevices" :key="teamDevice.id">
                          <td><strong>{{ teamDevice.device.name }}</strong></td>
                          <td class="d-none d-sm-table-cell"><code>{{ teamDevice.device.serial }}</code></td>
                          <td class="d-none d-md-table-cell">{{ teamDevice.device.type }}</td>
                          <td>
                            <b-badge variant="primary">{{ teamDevice.team.name }}</b-badge>
                          </td>
                          <td>
                            <b-badge :variant="getPermissionVariant(teamDevice.permission)">
                              {{ teamDevice.permission }}
                            </b-badge>
                          </td>
                          <td class="text-right">
                            <b-dropdown size="sm" variant="outline-secondary" right no-caret>
                              <template #button-content>
                                <i class="fas fa-ellipsis-v"></i>
                              </template>
                              <b-dropdown-item @click="openEditPermissionModal(teamDevice)">
                                <i class="fas fa-edit mr-1"></i> Edit Permission
                              </b-dropdown-item>
                              <b-dropdown-item variant="danger" @click="confirmRemoveDevice(teamDevice)">
                                <i class="fas fa-times mr-1"></i> Remove
                              </b-dropdown-item>
                            </b-dropdown>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else class="text-center text-muted">
                    <p>No team device assignments found.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Member Modal -->
    <b-modal
      v-model="showAddMemberModal"
      title="Add Team Member"
      @ok="addMember"
      @cancel="resetAddMemberForm"
    >
      <b-form-group label="Team" label-for="member-team">
        <b-form-select
          id="member-team"
          v-model="addMemberForm.teamId"
          :options="teamOptions"
          required
        />
      </b-form-group>
      <b-form-group label="User" label-for="member-user">
        <b-form-select
          id="member-user"
          v-model="addMemberForm.userId"
          :options="userOptions"
          required
        />
      </b-form-group>
      <b-form-group label="Role" label-for="member-role">
        <b-form-select
          id="member-role"
          v-model="addMemberForm.role"
          :options="roleOptions"
          required
        />
      </b-form-group>
    </b-modal>

    <!-- Add Device Modal -->
    <b-modal
      v-model="showAddDeviceModal"
      title="Add Device to Team"
      @ok="addDevice"
      @cancel="resetAddDeviceForm"
    >
      <b-form-group label="Team" label-for="device-team">
        <b-form-select
          id="device-team"
          v-model="addDeviceForm.teamId"
          :options="teamOptions"
          required
        />
      </b-form-group>
      <b-form-group label="Device" label-for="device-device">
        <b-form-select
          id="device-device"
          v-model="addDeviceForm.deviceId"
          :options="deviceOptions"
          required
        />
      </b-form-group>
      <b-form-group label="Permission" label-for="device-permission">
        <b-form-select
          id="device-permission"
          v-model="addDeviceForm.permission"
          :options="permissionOptions"
          required
        />
      </b-form-group>
    </b-modal>

    <!-- Edit Permission Modal -->
    <b-modal
      v-model="showEditPermissionModal"
      title="Edit Device Permission"
      @ok="updatePermission"
      @cancel="resetEditPermissionForm"
    >
      <p v-if="editPermissionForm.deviceName" class="text-muted">
        Editing permission for <strong>{{ editPermissionForm.deviceName }}</strong>
        in <strong>{{ editPermissionForm.teamName }}</strong>
      </p>
      <b-form-group label="Permission" label-for="edit-permission">
        <b-form-select
          id="edit-permission"
          v-model="editPermissionForm.permission"
          :options="permissionOptions"
          required
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import {
  TEAMS_QUERY,
  TEAM_MEMBERS_QUERY,
  TEAM_DEVICES_QUERY,
  ALL_USERS_QUERY,
  ALL_DEVICES_QUERY,
  ADD_USER_TO_TEAM_MUTATION,
  REMOVE_USER_FROM_TEAM_MUTATION,
  ADD_DEVICE_TO_TEAM_MUTATION,
  UPDATE_TEAM_DEVICE_MUTATION,
  REMOVE_DEVICE_FROM_TEAM_MUTATION
} from '@/constants/graphql'

export default {
  name: 'TeamManagement',
  data () {
    return {
      showAddMemberModal: false,
      showAddDeviceModal: false,
      showEditPermissionModal: false,
      addMemberForm: { teamId: null, userId: null, role: 'CUSTOMER' },
      addDeviceForm: { teamId: null, deviceId: null, permission: 'READ' },
      editPermissionForm: { id: null, permission: 'READ', deviceName: '', teamName: '' },
      roleOptions: [
        { value: 'CUSTOMER', text: 'Customer' },
        { value: 'READONLY', text: 'Read Only' },
        { value: 'ADMIN', text: 'Admin' }
      ],
      permissionOptions: [
        { value: 'READ', text: 'Read' },
        { value: 'WRITE', text: 'Write' },
        { value: 'FULL', text: 'Full' }
      ]
    }
  },
  computed: {
    teamOptions () {
      if (!this.teams) return []
      return this.teams.map(t => ({ value: t.id, text: t.name }))
    },
    userOptions () {
      if (!this.users) return []
      return this.users.map(u => ({ value: u.id, text: `${u.firstName} ${u.lastName} (${u.email})` }))
    },
    deviceOptions () {
      if (!this.devices) return []
      return this.devices.map(d => ({ value: d.id, text: `${d.name} — ${d.serial}` }))
    }
  },
  methods: {
    getTeamMemberCount (teamId) {
      if (!this.teamMembers) return 0
      return this.teamMembers.filter(m => m.team.id === teamId).length
    },
    getTeamDeviceCount (teamId) {
      if (!this.teamDevices) return 0
      return this.teamDevices.filter(d => d.team.id === teamId).length
    },
    getRoleVariant (role) {
      const map = { ADMIN: 'danger', CUSTOMER: 'primary', READONLY: 'secondary' }
      return map[role] || 'info'
    },
    getPermissionVariant (permission) {
      const map = { FULL: 'success', WRITE: 'warning', READ: 'info' }
      return map[permission] || 'secondary'
    },
    openAddMemberModal () {
      this.showAddMemberModal = true
    },
    openAddDeviceModal () {
      this.showAddDeviceModal = true
    },
    openEditPermissionModal (teamDevice) {
      this.editPermissionForm = {
        id: teamDevice.id,
        permission: teamDevice.permission,
        deviceName: teamDevice.device.name,
        teamName: teamDevice.team.name
      }
      this.showEditPermissionModal = true
    },
    addMember () {
      const { teamId, userId, role } = this.addMemberForm
      if (!teamId || !userId || !role) return
      this.$apollo.mutate({
        mutation: ADD_USER_TO_TEAM_MUTATION,
        variables: { teamId, userId, role }
      }).then(() => {
        this.$swal.fire({ icon: 'success', title: 'Member added successfully!' })
        this.resetAddMemberForm()
        this.$apollo.queries.teamMembers.refetch()
      }).catch(() => {})
    },
    confirmRemoveMember (member) {
      this.$swal.fire({
        title: 'Remove member?',
        text: `Remove ${member.user.firstName} ${member.user.lastName} from ${member.team.name}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove',
        timer: 0,
        showConfirmButton: true
      }).then(result => {
        if (result.isConfirmed) {
          this.$apollo.mutate({
            mutation: REMOVE_USER_FROM_TEAM_MUTATION,
            variables: { id: member.id }
          }).then(() => {
            this.$swal.fire({ icon: 'success', title: 'Member removed.', timer: 0, showConfirmButton: true, confirmButtonText: 'OK' })
            this.$apollo.queries.teamMembers.refetch()
          }).catch(() => {})
        }
      })
    },
    addDevice () {
      const { teamId, deviceId, permission } = this.addDeviceForm
      if (!teamId || !deviceId || !permission) return
      this.$apollo.mutate({
        mutation: ADD_DEVICE_TO_TEAM_MUTATION,
        variables: { teamId, deviceId, permission }
      }).then(() => {
        this.$swal.fire({ icon: 'success', title: 'Device added successfully!' })
        this.resetAddDeviceForm()
        this.$apollo.queries.teamDevices.refetch()
      }).catch(() => {})
    },
    updatePermission () {
      const { id, permission } = this.editPermissionForm
      this.$apollo.mutate({
        mutation: UPDATE_TEAM_DEVICE_MUTATION,
        variables: { id, permission }
      }).then(() => {
        this.$swal.fire({ icon: 'success', title: 'Permission updated.' })
        this.showEditPermissionModal = false
        this.$apollo.queries.teamDevices.refetch()
      }).catch(() => {})
    },
    confirmRemoveDevice (teamDevice) {
      this.$swal.fire({
        title: 'Remove device?',
        text: `Remove ${teamDevice.device.name} from ${teamDevice.team.name}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove',
        timer: 0,
        showConfirmButton: true
      }).then(result => {
        if (result.isConfirmed) {
          this.$apollo.mutate({
            mutation: REMOVE_DEVICE_FROM_TEAM_MUTATION,
            variables: { id: teamDevice.id }
          }).then(() => {
            this.$swal.fire({ icon: 'success', title: 'Device removed.', timer: 0, showConfirmButton: true, confirmButtonText: 'OK' })
            this.$apollo.queries.teamDevices.refetch()
          }).catch(() => {})
        }
      })
    },
    resetAddMemberForm () {
      this.addMemberForm = { teamId: null, userId: null, role: 'CUSTOMER' }
    },
    resetAddDeviceForm () {
      this.addDeviceForm = { teamId: null, deviceId: null, permission: 'READ' }
    },
    resetEditPermissionForm () {
      this.editPermissionForm = { id: null, permission: 'READ', deviceName: '', teamName: '' }
    }
  },
  apollo: {
    teams: { query: TEAMS_QUERY },
    teamMembers: { query: TEAM_MEMBERS_QUERY },
    teamDevices: { query: TEAM_DEVICES_QUERY },
    users: { query: ALL_USERS_QUERY },
    devices: { query: ALL_DEVICES_QUERY }
  }
}
</script>

<style scoped>
.team-management {
  padding: 20px;
}

.table th {
  border-top: none;
  font-weight: 600;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

code {
  background-color: #f8f9fa;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 0.875em;
}

.btn-group .btn {
  margin-right: 2px;
}

.btn-group .btn:last-child {
  margin-right: 0;
}
</style>
